import type { SelectableMedia } from '@/types/media';

export default function useCampsiteMedia<T extends SelectableMedia>({
  media: pMedia = [],
  mediaElements = new Map(),
}: {
  media: MaybeRefOrGetter<T[]>;
  mediaElements?: Map<number, HTMLElement>;
}) {
  /********************
   * REFS & VARS       *
   ********************/
  const media = toRef(pMedia) as Ref<T[]>;
  const selectedItemIdx = ref<number | null>(null);

  /********************
   * FUNCTIONS         *
   ********************/
  function onKeyUp(event: KeyboardEvent) {
    // ArrowLeft, ArrowRight, Escape
    if (event.key === 'ArrowLeft') {
      selectNextMediaItem(-1);
    } else if (event.key === 'ArrowRight') {
      selectNextMediaItem(1);
    } else if (event.key === 'Escape') {
      deselectCurrentMediaItem();
    }
  }

  function getMediaItemByIndex(idx: number): T | null {
    if (!media.value.length) {
      return null;
    }

    const items = media.value.filter(item => item.idx === idx);
    if (!items.length) {
      return null;
    }

    return items[0] as T ?? null;
  }

  function getNextMediaItemIndex(currentIdx: number | null, direction: -1 | 1 = 1): number | null {
    if (!media.value.length) {
      return null;
    }

    if (currentIdx === null && direction === 1) {
      currentIdx = -1;
    }

    let items: T[] = [];
    if (direction > 0 && currentIdx !== null) {
      items = media.value.filter(item => item.idx > currentIdx);
    } else if (direction < 0 && currentIdx !== null) {
      items = media.value.filter(item => item.idx < currentIdx);
    }

    if (!items.length) {
      return null;
    }

    const item = direction > 0 ? items[0] : items[items.length - 1];
    return item?.idx ?? null;
  }

  function selectNextMediaItem(direction: -1 | 1) {
    const newIdx = getNextMediaItemIndex(selectedItemIdx.value, direction);

    if (newIdx === null) {
      return;
    }
    selectedItemIdx.value = newIdx;

    toggleSelectedMedia(
      getMediaItemByIndex(selectedItemIdx.value),
      mediaElements.get(selectedItemIdx.value)?.firstElementChild as HTMLElement | null,
      true,
    );
  }

  function deselectCurrentMediaItem() {
    if (selectedItemIdx.value === null) {
      return;
    }

    toggleSelectedMedia(
      getMediaItemByIndex(selectedItemIdx.value),
      mediaElements.get(selectedItemIdx.value)?.firstElementChild as HTMLElement | null,
      false,
    );
  }

  function onMediaItemClicked(event: Event, item: T, offset = 0) {
    selectedItemIdx.value = item.idx;
    toggleSelectedMedia(item, event.currentTarget as HTMLElement, null, offset);
  }

  function toggleSelectedMedia(
    item: T | null,
    htmlElement: Element | HTMLElement | null = null,
    selected: boolean | null = null,
    offset = 0,
  ) {
    if (!item) {
      return;
    }

    item.selected = selected ?? !item.selected;

    if (htmlElement) {
      nextTick(() => {
        if (offset > 0) {
          window.scroll({ top: getOffsetTop(htmlElement) - offset, behavior: 'smooth' });
        } else {
          htmlElement.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  }

  return {
    onKeyUp,
    onMediaItemClicked,
    selectedItemIdx,
  };
}
